import api from './api';

/**
 * Check if push notifications are supported
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check if user is authenticated (has valid token)
 */
export function isUserAuthenticated() {
  const token = localStorage.getItem('accessToken');
  return !!token;
}

/**
 * Check if notification permission is granted
 */
export function isPermissionGranted() {
  return Notification.permission === 'granted';
}

/**
 * Check if notification permission is denied
 */
export function isPermissionDenied() {
  return Notification.permission === 'denied';
}

/**
 * Request notification permission
 * @returns {Promise<NotificationPermission>}
 */
export async function requestPermission() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return 'denied';
  }

  try {
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return 'denied';
  }
}

/**
 * Register service worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log('Service Worker registered:', registration.scope);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Get VAPID public key (from env or server)
 * @returns {Promise<string|null>}
 */
export async function getVapidPublicKey() {
  // First try environment variable (faster, no network)
  const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (envKey) {
    console.log('[Push] Using VAPID key from environment');
    return envKey;
  }

  // Fall back to server
  try {
    console.log('[Push] Fetching VAPID key from server...');
    const response = await api.get('/push/vapid-public-key');
    return response.data.data.publicKey;
  } catch (error) {
    console.error('[Push] Failed to get VAPID public key:', error);
    return null;
  }
}

/**
 * Convert URL-safe base64 to Uint8Array
 * @param {string} base64String
 * @returns {Uint8Array}
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 * IMPORTANT: Call this directly from a click handler - permission must be requested
 * immediately on user gesture, before any async work.
 * @param {boolean} skipServerSave - If true, only does browser subscription without saving to server
 * @returns {Promise<{success: boolean, subscription?: object, error?: string, needsAuth?: boolean}>}
 */
export async function subscribeToPush(skipServerSave = false) {
  console.log('[Push] Starting subscription flow...');

  if (!isPushSupported()) {
    console.warn('[Push] Push not supported in this browser');
    return { success: false, error: 'Push notifications not supported in this browser' };
  }

  // Check authentication before starting (only if we need to save to server)
  if (!skipServerSave && !isUserAuthenticated()) {
    console.warn('[Push] User not authenticated - cannot save subscription to server');
    return { success: false, error: 'Please log in to enable notifications', needsAuth: true };
  }

  try {
    // 1. REQUEST PERMISSION FIRST - Must happen immediately on user gesture!
    console.log('[Push] Step 1: Checking permission... Current:', Notification.permission);
    if (Notification.permission === 'default') {
      console.log('[Push] Requesting permission from browser NOW...');
      // This MUST be the first async call to preserve user gesture context
      const permission = await Notification.requestPermission();
      console.log('[Push] Permission result:', permission);
      if (permission !== 'granted') {
        return { success: false, error: 'Permission denied' };
      }
    } else if (Notification.permission === 'denied') {
      console.log('[Push] Permission was previously denied');
      return { success: false, error: 'Notifications are blocked. Please enable them in browser settings.' };
    } else {
      console.log('[Push] Permission already granted');
    }

    // 2. Get VAPID public key
    console.log('[Push] Step 2: Getting VAPID key...');
    const vapidPublicKey = await getVapidPublicKey();
    if (!vapidPublicKey) {
      console.error('[Push] No VAPID key available');
      return { success: false, error: 'Push notifications not configured. Please contact support.' };
    }
    console.log('[Push] VAPID key obtained');

    // 3. Register service worker
    console.log('[Push] Step 3: Registering service worker...');
    const registration = await registerServiceWorker();
    if (!registration) {
      console.error('[Push] Service worker registration failed');
      return { success: false, error: 'Service worker registration failed' };
    }
    console.log('[Push] Service worker registered');

    // Wait for service worker to be ready
    console.log('[Push] Waiting for service worker to be ready...');
    await navigator.serviceWorker.ready;
    console.log('[Push] Service worker ready');

    // 4. Check existing subscription
    console.log('[Push] Step 4: Checking existing subscription...');
    let subscription = await registration.pushManager.getSubscription();

    // 5. Create new subscription if none exists
    if (!subscription) {
      console.log('[Push] Step 5: Creating new subscription...');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      console.log('[Push] New subscription created');
    } else {
      console.log('[Push] Using existing subscription');
    }

    const subscriptionJSON = subscription.toJSON();

    // 6. Send subscription to server (if not skipping)
    if (!skipServerSave) {
      console.log('[Push] Step 6: Saving subscription to server...');
      try {
        await api.post('/push/subscribe', {
          subscription: {
            endpoint: subscriptionJSON.endpoint,
            keys: {
              p256dh: subscriptionJSON.keys.p256dh,
              auth: subscriptionJSON.keys.auth,
            },
          },
        });
        console.log('[Push] Subscription saved to server successfully!');
      } catch (serverError) {
        console.error('[Push] Failed to save to server:', serverError);
        // Check if it's an auth error
        if (serverError.response?.status === 401) {
          return {
            success: false,
            error: 'Session expired. Please refresh the page and try again.',
            needsAuth: true,
            subscription: subscriptionJSON // Browser subscription succeeded
          };
        }
        // For other errors, still return partial success since browser subscription worked
        return {
          success: false,
          error: 'Browser notifications enabled, but failed to sync with server. Please try again.',
          subscription: subscriptionJSON
        };
      }
    } else {
      console.log('[Push] Skipping server save (skipServerSave=true)');
    }

    return { success: true, subscription: subscriptionJSON };
  } catch (error) {
    console.error('[Push] Subscription error:', error);
    return { success: false, error: error.message || 'Failed to enable notifications' };
  }
}

/**
 * Sync existing browser subscription to server
 * Call this when user logs in and already has browser notification permission
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function syncSubscriptionToServer() {
  if (!isPushSupported() || !isUserAuthenticated()) {
    return { success: false, error: 'Not supported or not authenticated' };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      return { success: false, error: 'No browser subscription found' };
    }

    const subscriptionJSON = subscription.toJSON();
    await api.post('/push/subscribe', {
      subscription: {
        endpoint: subscriptionJSON.endpoint,
        keys: {
          p256dh: subscriptionJSON.keys.p256dh,
          auth: subscriptionJSON.keys.auth,
        },
      },
    });

    console.log('[Push] Subscription synced to server');
    return { success: true };
  } catch (error) {
    console.error('[Push] Sync error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Unsubscribe from push notifications
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Unsubscribe from browser
      await subscription.unsubscribe();

      // Remove from server
      await api.post('/push/unsubscribe', {
        endpoint: subscription.endpoint,
      });

      console.log('Unsubscribed from push notifications');
    }

    return { success: true };
  } catch (error) {
    console.error('Unsubscribe error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get current subscription status
 * @returns {Promise<{subscribed: boolean, subscription?: object}>}
 */
export async function getSubscriptionStatus() {
  if (!isPushSupported()) {
    return { subscribed: false };
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    return {
      subscribed: !!subscription,
      subscription: subscription ? subscription.toJSON() : null,
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return { subscribed: false };
  }
}

/**
 * Send a test push notification
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function sendTestNotification() {
  try {
    const response = await api.post('/push/test');
    return { success: true, data: response.data.data };
  } catch (error) {
    console.error('Test notification error:', error);
    return { success: false, error: error.message };
  }
}
